import React, { useState, useEffect } from 'react';
import { uploadToExternalS3 } from './externalS3Upload';
import { fetchAuthSession } from 'aws-amplify/auth';
import { buildBomCsv } from './bomCsv';
import { buildBomCsvFilename } from './bomCsvFilename';
import { buildCsvQuoteNumber } from './exportFilename';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

/**
 * Dialog component for exporting BOM data to S3/CSV
 * This version accepts opportunityID as a prop and can auto-export
 * Uses the user's full name from Cognito attributes instead of username
 */
const ExportToS3Dialog = ({ 
  isOpen, 
  onClose, 
  bom, 
  config,       // Config holds all the accurate details we need. MORK TODO : Could make a data structure to pass into the CSV
  metrics, 
  opportunityID = '', 
  customerNumber = '', 
  offerNumber = '',   // MORK TODO : legacy value - to be phased out eventually
  preparedFor = '',
  onBeforeExport = async () => true
}) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportComplete, setExportComplete] = useState(false);
  const [error, setError] = useState('');
  const [isNameFetched, setIsNameFetched] = useState(false);

  /**
   * Confirm the Cognito session is live before the export auto-fires. The
   * user's name used to be read here as metadata for the Amplify-storage copy
   * of the CSV, which CONF-236 removed; only the readiness signal remains.
   * fetchAuthSession answers it from the cached tokens. The previous
   * fetchUserAttributes() call hit Cognito GetUser, which needs the
   * aws.cognito.signin.user.admin scope the hardened app clients do not grant,
   * so it 400'd (caught, but logged) on every production export. App.js:187
   * avoids GetUser for the same reason.
   */
  useEffect(() => {
    const getUserAttributes = async () => {
      try {
        await fetchAuthSession();
      } catch (error) {
        console.error('Error confirming the session before export:', error);
      } finally {
        setIsNameFetched(true);
      }
    };

    if (isOpen) {
      getUserAttributes();
    }
  }, [isOpen]);

  /**
   * Validates the customer ID format
   * Must be exactly 18 characters and alphanumeric
   */
  const validateOpportunityID = (id) => {
    if (!id || id.length !== 18) {
      return "Opportunity ID must be exactly 18 characters";
    }

    if (!/^[A-Za-z0-9]+$/.test(id)) {
      return "Customer ID can only contain letters and numbers";
    }

    return ""; // Return empty string if valid
  };

  /**
   * Formats a date string to MM/DD/YYYY
   * @param {*} dateString 
   * @returns formatted date
   */
  const formatDateMMDDYYYY = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  };

  /**
   * Handles the export process
   * Creates a CSV file from BOM data and uploads to S3
   */
  const handleExport = async () => {
    // Validate the customer ID
    const validationError = validateOpportunityID(opportunityID);
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsExporting(true);
      setExportComplete(false);
      setError('');

      // Create formatted dates for the CSV
      const now = new Date();
      const currentDate = formatDateMMDDYYYY(now);

      
      // Calculate expiration date (1 month from now)
      // const expirationDate = new Date(now);
      // expirationDate.setMonth(expirationDate.getMonth() + 1);
      // const expDateStr = expirationDate.toLocaleDateString('en-US');

      // Create Offer Number Combined from Salesforce Quote Number and Configurator Serial Number.
      // CONF-214: the serial arrives as "#1234"; the cell carries the bare serial.
      const officialQuoteNumber = buildCsvQuoteNumber(config);

      // Rows + text live in bomCsv.js (CONF-242: formula-leading cells are
      // prefixed there; column order is pinned by bomCsv.test.js).
      const csv = buildBomCsv({
        bom, config, preparedFor, currentDate,
        quoteNumber: officialQuoteNumber,
        quoteExpires: formatDateMMDDYYYY(config.quoteExpirationDate),
      });

      // Quote numbers are sanitised in the filename (the serial carries a
      // leading '#'), as is the Quote Number cell above.
      const filename = buildBomCsvFilename(config, now);

      // Audit hook: log-only check before the upload proceeds (opening flows
      // have already confirmed with the user — this is not a confirm gate)
      if (!(await onBeforeExport(filename))) {
        setError('Export blocked — audit log unavailable.');
        return;
      }

      // Upload to the quote-explorer bucket — the only server-side copy since
      // CONF-236 (the Amplify-storage copy wrote to a public/ prefix every
      // signed-in role could read, and never worked on Gen2 builds anyway).
      try {
        await uploadToExternalS3(filename, csv, 'text/csv');
      } catch (err) {
        console.error('External S3 CSV upload failed:', err);
      }

      // Also trigger download for the user
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.setAttribute('hidden', '');
      a.setAttribute('href', url);
      a.setAttribute('download', filename);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      setExportComplete(true);
      
      // Auto-close the dialog after a delay if export was successful
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error('Error exporting BOM:', error);
      setError(`Error exporting BOM: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Auto-export when the dialog is opened (if we have a valid Opportunity ID)
  // Only trigger export after name has been fetched
  useEffect(() => {
    if (isOpen && opportunityID && isNameFetched) {
      handleExport();
    }
  }, [isOpen, isNameFetched, opportunityID]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-96 bg-gray-800">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-white">BOM Export Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Status message */}
            <div className="text-center py-4">
              {error && (
                <div className="text-red-500 mb-4">
                  {error}
                </div>
              )}
              {isExporting && (
                <div className="text-white flex flex-col items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white mb-4"></div>
                  <p>Exporting BOM data...</p>
                </div>
              )}
              {exportComplete && !error && (
                <div className="text-green-400">
                  <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p>Export completed successfully!</p>
                  <p className="text-sm text-gray-300 mt-2">The CSV file has been downloaded to your computer.</p>
                </div>
              )}
            </div>
            
            {/* Buttons */}
            <div className="flex justify-end space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded text-white hover:bg-gray-700"
              >
                Close
              </button>
              {error && (
                <button
                  onClick={handleExport}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                  disabled={isExporting}
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExportToS3Dialog;